import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * Usage meter progress bar for rate-limited features.
 * Only renders for features with per_day/per_week/per_month limits.
 */
export default function UsageMeter({ featureKey, label, showLabel = true, compact = false }) {
  const { getUsage, getLimit } = useSubscription();
  const limit = getLimit(featureKey);
  const usage = getUsage(featureKey);

  // Don't render for boolean or unlimited features
  if (!limit || limit.limit_type === 'unlimited' || limit.limit_type === 'boolean') {
    return null;
  }

  const used = usage.used || 0;
  const max = usage.limit || limit.limit_value || 0;
  if (max === 0) return null;

  const percent = Math.min((used / max) * 100, 100);
  const isNearLimit = percent >= 80;
  const isAtLimit = percent >= 100;

  const periodLabel = limit.limit_type === 'per_week' ? '/week'
    : limit.limit_type === 'per_month' ? '/mo'
    : limit.limit_type === 'per_day' ? '/day' : '';

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-primary'
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span>{used}/{max}</span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {showLabel && (
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">{label || featureKey}</span>
          <span className={`font-medium ${isAtLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-foreground'}`}>
            {used} / {max}{periodLabel}
          </span>
        </div>
      )}
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-primary'
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {usage.resets && isNearLimit && (
        <p className="text-xs text-muted-foreground">
          Resets {new Date(usage.resets).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}
